<script setup>
import { ref, computed, watch, onMounted } from 'vue'

// Renders CMS HTML inside a shadow root: site CSS does not reach it and its own <style> does not leak out.
// `all: initial` on the host cuts inherited properties (font, color, line-height…); CSS custom properties
// still inherit, so the markup can use the site's --font-family-* variables, and @font-face rules loaded by
// the page apply inside the shadow tree.
const props = defineProps({
  html: {
    type: String,
    default: ''
  }
})

const host = ref(null)

const HOST_RESET = '<style>:host{all:initial;display:block}</style>'

// Same underline as styles/inline-links.css. Appended after the CMS markup so it
// overrides in-shadow rules like .rev-steps__link { text-decoration: underline }.
const SHADOW_INLINE_LINKS = `<style>
a[href]{
  cursor:pointer;
  position:relative;
  color:inherit;
  text-decoration:none!important;
  background-image:linear-gradient(currentColor,currentColor);
  background-position:0 100%;
  background-repeat:no-repeat;
  background-size:100% .05rem;
  transition:background-size .2s ease-out;
}
@media (hover:hover){
  a[href]:hover{
    background-position:100% 100%;
    background-size:0% .05rem;
  }
}
</style>`

// Steward chat bubbles: Figma 5182:5307 / 5182:5339 — 8% glass, not opaque #F2F2F0.
// Appended after CMS markup so it beats the in-shadow --sf-bubble / --sf-ink rules.
const SHADOW_STEWARD_BUBBLE = `<style>
.steward-flow{--sf-bubble:rgba(28,33,26,.08)}
.steward-flow__bubble{
  background:rgba(28,33,26,.08)!important;
  color:#fff!important;
  -webkit-backdrop-filter:blur(8.4px);
  backdrop-filter:blur(8.4px);
}
.steward-flow__name:not(.steward-flow__name--bot){color:#fff!important}
</style>`

// Korean translation of the steward conversation HTML rewrote English
// fragments inside the inline Rosa JPEG (To → ro, plus an extra a),
// which breaks the data URI. Restore the working English encoding.
const repairStewardAvatar = (html) => {
  if (!html || !html.includes('steward-flow__avatar')) return html
  return html
    .replace('/E/rror4c/', '/E/rTor4c/')
    .replace('S54baraN', 'S54braN')
}

const shadowMarkup = computed(() => HOST_RESET + repairStewardAvatar(props.html || '') + SHADOW_INLINE_LINKS + SHADOW_STEWARD_BUBBLE)

// SSR / prerender: declarative shadow DOM, attached by the browser's HTML parser before hydration,
// so the content is in the static HTML and does not shift the layout when JS loads.
const ssrMarkup = computed(() => `<template shadowrootmode="open">${shadowMarkup.value}</template>`)

// Client-side navigation: v-html (innerHTML) does not parse declarative shadow DOM, so attach it here.
const render = () => {
  const el = host.value
  if (!el) return

  const root = el.shadowRoot || el.attachShadow({ mode: 'open' })
  root.innerHTML = shadowMarkup.value
}

onMounted(() => {
  // Already attached from the server-rendered HTML
  if (host.value?.shadowRoot) return
  render()
})

watch(shadowMarkup, render)
</script>

<template>
  <div ref="host" v-html="ssrMarkup" />
</template>
